/**
 * # AsInfoPopup Component
 *
 * A persistent popup card fixed to the bottom-right of the screen, embedding
 * an embed content module via `as-embed-content`. Remembers dismissal in
 * localStorage so it only shows once per user/browser.
 *
 * ## Attributes
 * - `embed-module-key`: Embed location key passed to the embeds API (required)
 * - `popup-id`: Unique ID used for the localStorage dismissed key.
 *               Defaults to the value of `embed-module-key`.
 * - `delay`: Milliseconds before the popup appears (default: 0)
 *
 * ## Storage
 * Uses `localStorage` key `as-info-popup:dismissed:<popup-id>`.
 * Call `AsInfoPopup.reset(popupId)` to clear a dismissal (e.g. for testing).
 *
 * ## Events listened (on window)
 * - `as-info-popup-reset`: Resets dismissal for the popup whose `popup-id`
 *   matches `event.detail.popupId`, then re-shows it.
 *
 * ## Usage
 * ```html
 * <as-info-popup
 *   embed-module-key="home-banner"
 *   title-text="Welcome"
 *   delay="2000">
 * </as-info-popup>
 * ```
 */

import '../embeds/as-embed-content.ts';
import '../inputs/buttons/as-button.ts';
import { isOnPrem } from '../../data/constants.ts';
import styleText from './as-info-popup.css?raw';
import { sendEv } from '../../data/events.ts';

const STORAGE_PREFIX = 'as-info-popup:dismissed:';

export class AsInfoPopup extends HTMLElement {
  static readonly tag = 'as-info-popup' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _delayTimer: ReturnType<typeof setTimeout> | null = null;
  private _boundHandleReset: (e: Event) => void;
  private _boundHandleEmbedReady: (e: Event) => void;
  private _isOpen = false;
  private _everOpened = false;
  private _currentIndex = 0;
  private _keys: string[] = [];
  private _virtualSlideCount = 0;

  /** Clears the dismissed flag for a given popup ID. */
  static reset(popupId: string): void {
    localStorage.removeItem(`${STORAGE_PREFIX}${popupId}`);
  }

  private static _isDismissed(popupId: string): boolean {
    return !!localStorage.getItem(`${STORAGE_PREFIX}${popupId}`);
  }

  private static _setDismissed(popupId: string): void {
    localStorage.setItem(`${STORAGE_PREFIX}${popupId}`, '1');
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsInfoPopup._sheet];
    this._boundHandleReset = this._handleReset.bind(this);
    this._boundHandleEmbedReady = this._handleEmbedReady.bind(this);
  }

  connectedCallback(): void {
    if (isOnPrem) return;
    window.addEventListener('as-info-popup-reset', this._boundHandleReset);
    this.addEventListener('as-embed-ready', this._boundHandleEmbedReady);
    this._mount();
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-info-popup-reset', this._boundHandleReset);
    this.removeEventListener('as-embed-ready', this._boundHandleEmbedReady);
    if (this._delayTimer !== null) clearTimeout(this._delayTimer);
  }

  private _popupId(): string {
    const rawKeys = this.getAttribute('embed-module-key') || 'default';
    return this.getAttribute('popup-id') || (rawKeys.includes(',') ? rawKeys.split(',')[0] : rawKeys);
  }

  private _handleEmbedReady(e: Event): void {
    const { count } = (e as CustomEvent).detail ?? {};
    // Only auto-expand if we have exactly one key (one as-embed-content)
    if (this._keys.length === 1 && count > 1) {
      this._virtualSlideCount = count;
      // A re-init can come back with fewer items than last time, so don't
      // leave the index pointing past the end — the dot for it wouldn't exist.
      if (this._currentIndex >= count) this._currentIndex = 0;
      this._renderNavigation();

      // Make sure the embed is showing the slide the dots say it is.
      const embed = this.shadowRoot!.querySelector('as-embed-content');
      if (embed && !embed.hasAttribute('index')) {
        embed.setAttribute('index', this._currentIndex.toString());
      }
    }
  }

  private _mount(): void {
    const embedKeysRaw = this.getAttribute('embed-module-key') || '';
    this._keys = embedKeysRaw.split(',').map(k => k.trim()).filter(k => !!k);

    // mount() rebuilds the shadow DOM from scratch, so start back at the first
    // slide. On a re-mount (as-info-popup-reset) a stale index would mark the
    // wrong dot active, and the embed's slide count is unknown again until the
    // fresh as-embed-ready arrives.
    this._currentIndex = 0;
    this._virtualSlideCount = 0;

    const openIcon  = this.getAttribute('open-icon')  || '';
    const closeIcon = this.getAttribute('close-icon') || '';

    const slidesHtml = this._keys.map((key, i) => `
      <div class="slide ${i === this._currentIndex ? 'active' : ''}" data-index="${i}">
        <as-embed-content embed-module-key="${key}"></as-embed-content>
      </div>
    `).join('');

    this.shadowRoot!.innerHTML = /*html*/`
      <div class="popup">
        <div class="popup-body">
          <div class="slides-container">
            ${slidesHtml}
          </div>
          <div class="nav-placeholder"></div>
        </div>
      </div>
      <div class="btn-area">
        <as-button class="btn-close" file-name="icons/${closeIcon}" name="popup-toggle"></as-button>
        <as-button class="btn-open"  file-name="icons/${openIcon}"  name="popup-toggle"></as-button>
      </div>
    `;

    this.shadowRoot!.querySelector('.btn-close')!
      .addEventListener('as-button-click', () => this._toggle(false));
    this.shadowRoot!.querySelector('.btn-open')!
      .addEventListener('as-button-click', () => this._toggle(true));

    if (this._keys.length > 1) {
      this._renderNavigation();
    }

    if (AsInfoPopup._isDismissed(this._popupId())) {
      this._everOpened = true;
      this._isOpen     = false;
      this._syncState();
    } else {
      this._isOpen     = false;
      this._everOpened = false;
      this._syncState();
      const delay = parseInt(this.getAttribute('delay') || '0', 10);
      this._delayTimer = delay > 0
        ? setTimeout(() => this._toggle(true), delay)
        : null;
      if (!this._delayTimer) this._toggle(true);
    }
  }

  private _renderNavigation(): void {
    const total = this._keys.length > 1 ? this._keys.length : this._virtualSlideCount;
    if (total <= 1) return;

    const prevIcon  = this.getAttribute('prev-icon')  || 'arrow-left.svg';
    const nextIcon  = this.getAttribute('next-icon')  || 'arrow-right.svg';

    const navContainer = this.shadowRoot!.querySelector('.nav-placeholder');
    if (!navContainer) return;

    navContainer.innerHTML = `
      <div class="nav-controls">
        <as-button class="btn-prev" file-name="icons/${prevIcon}" black></as-button>
        <div class="breadcrumbs">
          ${Array.from({ length: total }).map((_, i) => `<div class="dot ${i === this._currentIndex ? 'active' : ''}" data-index="${i}"></div>`).join('')}
        </div>
        <as-button class="btn-next" file-name="icons/${nextIcon}" black></as-button>
      </div>
    `;

    navContainer.querySelector('.btn-prev')?.addEventListener('as-button-click', () => this._step(-1));
    navContainer.querySelector('.btn-next')?.addEventListener('as-button-click', () => this._step(1));
    navContainer.querySelectorAll('.dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const idx = parseInt((dot as HTMLElement).dataset.index || '0', 10);
        this._goTo(idx);
      });
    });
  }

  private _step(dir: number): void {
    const total = this._keys.length > 1 ? this._keys.length : this._virtualSlideCount;
    if (total <= 1) return;
    const nextIdx = (this._currentIndex + dir + total) % total;
    this._goTo(nextIdx);
  }

  private _goTo(index: number): void {
    this._currentIndex = index;
    
    if (this._keys.length > 1) {
      const slides = this.shadowRoot!.querySelectorAll('.slide');
      slides.forEach((s, i) => s.classList.toggle('active', i === index));
    } else {
      const embed = this.shadowRoot!.querySelector('as-embed-content');
      if (embed) embed.setAttribute('index', index.toString());
    }

    const dots = this.shadowRoot!.querySelectorAll('.dot');
    dots.forEach((d, i) => d.classList.toggle('active', i === index));
  }

  private _toggle(open: boolean): void {
    this._isOpen     = open;
    this._everOpened = true;

    const popup = this.shadowRoot!.querySelector('.popup') as HTMLElement | null;
    if (!popup) return;

    if (open) {
      AsInfoPopup.reset(this._popupId());
      popup.style.display = 'block';
      requestAnimationFrame(() => {
        popup.classList.add('visible');
        popup.classList.remove('hiding');
      });
    } else {
      AsInfoPopup._setDismissed(this._popupId());
      popup.classList.remove('visible');
      popup.classList.add('hiding');
      popup.addEventListener('transitionend', () => {
        popup.style.display = 'none';
      }, { once: true });
    }

    this._syncState();
  }

  private _syncState(): void {
    const btnClose = this.shadowRoot?.querySelector('.btn-close') as HTMLElement | null;
    const btnOpen  = this.shadowRoot?.querySelector('.btn-open')  as HTMLElement | null;
    if (!btnClose || !btnOpen) return;

    if (!this._everOpened) {
      btnClose.style.display = 'none';
      btnOpen.style.display  = 'none';
    } else if (this._isOpen) {
      btnClose.style.display = '';
      btnOpen.style.display  = 'none';
    } else {
      btnClose.style.display = 'none';
      btnOpen.style.display  = '';
    }
  }

  private _handleReset(e: Event): void {
    const { popupId } = (e as CustomEvent).detail ?? {};
    if (popupId && popupId !== this._popupId()) return;

    AsInfoPopup.reset(this._popupId());
    this._isOpen     = false;
    this._everOpened = false;
    if (this._delayTimer !== null) clearTimeout(this._delayTimer);
    this._mount();
  }
}

if (!customElements.get(AsInfoPopup.tag)) {
  customElements.define(AsInfoPopup.tag, AsInfoPopup);
} else {
  console.warn('as-info-popup already defined');
}

(window as any).resetPopups = () => {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  keys.forEach(k => localStorage.removeItem(k));
  sendEv(window, 'as-info-popup-reset', { detail: {} });
};
